import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import moment from 'moment';
import { concatMap, first } from 'rxjs/operators';
import { Router } from '@angular/router';

import { UserService } from '../../core/services/data/user.service';
import { FeaturedBooksService } from '../../core/services/data/featured-books.service';
import { GiveawayService } from '../../core/services/data/giveaway.service';
import { SitesService } from '../../core/services/data/sites.service';
import { SiteAdvertsService } from '../../core/services/data/site-adverts.service';
import { BooksService } from '../../core/services/data/books.service';
import { PurchaseProcessService } from '../../core/services/data/purchase-process.service';

import { IUser } from '../../types/datatypes';
import { PopulatedFeaturesAPIResponse, StatsBriefAPIResponse } from '../../types/responses';
import { IPopulatedFeature } from '../../types/populated';
import { AnalyticsService } from '../../core/services/analytics.service';
import { ModalService } from '../../core/services/modal.service';

// typescript for window with olark prop
interface customWindow extends Window {
	olark?: any;
}

declare const window: customWindow;

@Component({
	selector: 'app-promotions-widget',
	templateUrl: './promotions-widget.component.html',
	styleUrls: ['./promotions-widget.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class PromotionsWidgetComponent implements OnInit, OnDestroy {
	public author: IUser;

	public downloads: any = {};

	public promotions: IPopulatedFeature[] = [];

	public state = 'loading';

	public sitesById: any = {};

	public siteAdvertsById: any = {};

	public statistics = {
		features: 0,
		stacks: 0,
		books: 0,
	};

	private subscription$;

	private siteAdverts$;

	private sites$;

	constructor(
		private userService: UserService,
		private featuredBooksService: FeaturedBooksService,
		private giveawayService: GiveawayService,
		private sitesService: SitesService,
		private siteAdvertsService: SiteAdvertsService,
		private booksService: BooksService,
		private router: Router,
		private purchaseProcessService: PurchaseProcessService,
		private as: AnalyticsService,
		private ms: ModalService,
	) {

	}

	ngOnInit(): void {
		this.subscription$ = this.userService.author$
			.pipe(first())
			.pipe(concatMap((author: IUser) => {
				this.author = author;
				this.statistics.books = this.countBooks(author.books);
				return this.featuredBooksService.featureBrief(`owner=${author._id}`);
			}))
			.pipe(concatMap((result: PopulatedFeaturesAPIResponse) => {
				if (result.success) {
					this.statistics.features = result.features?.length;
					this.promotions = result.features.sort((a, b) => b.pubDate - a.pubDate).slice(0, 3);
					this.state = 'display';
					this.promotions.forEach((promotion) => this.getDownload(promotion._id));
				} else {
					this.state = 'error';
				}

				return this.featuredBooksService.statsBrief(`owner=${this.author._id}`);
			})).subscribe((result: StatsBriefAPIResponse) => {
				if (result.success) {
					this.statistics.features = result.features;
					this.statistics.stacks = result.stacks;
				}
			});

		this.sites$ = this.sitesService.sitesById$.subscribe((sitesById) => {
			this.sitesById = sitesById;
		});

		this.siteAdverts$ = this.siteAdvertsService.siteAdvertsById$.subscribe((siteAdvertsById) => {
			this.siteAdvertsById = siteAdvertsById;
		});
	}

	ngOnDestroy(): void {
		this.subscription$?.unsubscribe();
		this.sites$?.unsubscribe();
		this.siteAdverts$?.unsubscribe();
	}

	// Books can now be ASIN-less so we need to count by unique ID and unique ASIN.
	// Theres also "null", undefined, "", and null ASIN books, so we want to normalize that
	// and ignore the ASIN check if the asin isn't valid.

	countBooks(books: {_book: string; ASIN?: unknown}[]): number {
		let uniqueBookCount = 0;
		const asinSet = new Set();
		const bookIdSet = new Set();

		for (const book of books) {
			const normalizedAsin = this._normalizeASIN(book.ASIN);
			if (bookIdSet.has(book._book) === false && !(normalizedAsin && asinSet.has(book.ASIN))) {
				uniqueBookCount += 1;
				asinSet.add(book.ASIN);
				bookIdSet.add(book._book);
			}
		}

		return uniqueBookCount;
	}

	_normalizeASIN(asin: unknown): null | string {
		if (!asin) {
			return null;
		}

		if (typeof asin !== 'string' || asin?.length !== 10) {
			return null;
		}

		return asin;
	}

	getSiteNameById(id) {
		return this.sitesById[id]?.name ?? '';
	}

	getSiteAdvertNameById(id) {
		return this.siteAdvertsById[id]?.name ?? '';
	}

	isGiveaway(siteId: string) {
		// SiteGiveaway Migration
		return siteId === this.sitesService.GIVEAWAY_SITE_ID;
	}

	upcoming(pubDate) {
		return moment(pubDate, 'YYYYMMDD').isAfter(moment());
	}

	getStatus(siteId) {
		if (this.isGiveaway(siteId)) {
			return 'IN PROGRESS';
		}

		return 'UPCOMING';
	}

	getDownload(featureId) {
		this.giveawayService.getDownload(featureId, this.author._id).pipe(first()).subscribe((result: { success: boolean; url: string }) => {
			if (result.success) {
				this.downloads[featureId] = result.url;
			}
		});
	}

	download(promo) {
		const index = this.promotions.indexOf(promo);
		const options = {
			section: 2,
			section_headline: 'My Promotions',
			tile_placement: index + 1,
			tile_headline: promo._book.title,
			cta: 'Download',
		};
		this.as.homepageTracking(options);

		const url = this.downloads[promo._id];
		window.location = url;
	}

	formatDate(pubDate) {
		return moment(pubDate, 'YYYYMMDD').format('ddd, MMM Do YYYY');
	}

	goFeaturesEmpty() {
		const options = {
			section: 2.2,
			section_headline: 'My Promotions',
			tile_placement: 1,
			tile_headline: 'Unlock the magic of your author portal',
			cta: 'Run Your First Book Promo',
		};
		this.as.homepageTracking(options);

		this.router.navigate(['../features']);
	}

	goMyBooksEmpty() {
		const options = {
			section: 2.2,
			section_headline: 'My Promotions',
			tile_placement: 2,
			tile_headline: 'We\'re here to help',
			cta: 'Add Books',
		};
		this.as.homepageTracking(options);

		this.router.navigate(['../books']);
	}

	openSupportEmpty() {
		const options = {
			section: 2.2,
			section_headline: 'My Promotions',
			tile_placement: 3,
			tile_headline: 'We\'re here to help',
			cta: 'Ask a Real Person',
		};
		this.as.homepageTracking(options);

		// if (this.userService.isGoldMember() || this.userService.isPlatinumMember()) {
		// 	window.olark('api.box.expand');
		// 	return;
		// }

		// window.open('https://www.writtenwordmedia.com/contact-us/', '_blank');
		this.ms.open('contactFormModal');
	}

	rerun(promo) {
		const index = this.promotions.indexOf(promo);
		const options = {
			section: 2,
			section_headline: 'My Promotions',
			tile_placement: index + 1,
			tile_headline: promo._book.title,
			cta: 'Re-Run',
		};
		this.as.homepageTracking(options);

		const purchaseSource = 'Re-run promo';
		this.purchaseProcessService.setDetails({
			feature: {
				ASIN: promo._book.ASIN,
				whereAvailable: promo.whereAvailable,
				purchaseSource,
			},
			book: {
				ASIN: promo._book.ASIN,
				amazonLink: `https://www.amazon.com/dp/${promo._book.ASIN}`,
				whereAvailable: promo.whereAvailable,
				title: promo._book.title,
				author: promo._book.author,
				description: promo.description,
				bookLength: promo._book.bookLength,
				nameOfSeries: promo._book.nameOfSeries,
				bookNumber: promo._book.bookNumber,
				numberOfBooksInSeries: promo._book.numberOfBooksInSeries,
				threesomesOrRomanticScenes: promo._book.threesomesOrRomanticScenes,
				endingOfBook: promo._book.endingOfBook,
				cheatingOrInfidelity: promo._book.cheatingOrInfidelity,
				commonThemes: promo._book.commonThemes,
			},
		}, 'promotions-widget');

		let link = `/author-dashboard/${this.author.authorLink}/features/${this.sitesById[promo._site].seoSlug}`;
		if (promo._siteAdvert) {
			link += `/${this.siteAdvertsById[promo._siteAdvert].seoSlug}`;
		}
		this.router.navigate([link]).then();
	}

	canRerun(promo) {
		const isGiveaway = this.isGiveaway(promo._site);
		const siteAdvert = this.siteAdvertsById[promo._siteAdvert];

		return !isGiveaway && siteAdvert?.recurring.type !== 'never';
	}

	goFeatures() {
		const options = {
			section: 2.1,
			section_headline: 'My Promo Data',
			tile_placement: 1,
			tile_headline: 'Promotions Run All Time',
			cta: 'Run a Promotion',
		};
		this.as.homepageTracking(options);

		this.router.navigate(['../features']);
	}

	goPromostacks() {
		const options = {
			section: 2.1,
			section_headline: 'My Promo Data',
			tile_placement: 2,
			tile_headline: 'Stacks Run All Time',
			cta: 'Run a Stack',
		};
		this.as.homepageTracking(options);

		this.router.navigate(['../promo-stacks']);
	}

	goMyBooks() {
		const options = {
			section: 2.1,
			section_headline: 'My Promo Data',
			tile_placement: 3,
			tile_headline: 'Books Uploaded',
			cta: 'Upload Books',
		};
		this.as.homepageTracking(options);

		this.router.navigate(['../books']);
	}

	goFeaturesAction() {
		const options = {
			section: 2,
			section_headline: 'My Promotions',
			cta: 'Run a Promotion',
		};
		this.as.homepageTracking(options);

		this.router.navigate(['../features']);
	}

	goPromotionsAction() {
		const options = {
			section: 2,
			section_headline: 'My Promotions',
			cta: 'See All Promotions',
		};
		this.as.homepageTracking(options);

		this.router.navigate(['../promotions']);
	}
}
